import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import SearchIcon from '../../assets/icons/search.svg?react';
import ArrowDownIcon from '../../assets/icons/arrow-down.svg?react';

const SearchBox = forwardRef(({
  placeholder = '검색',
  filters = [],
  onFilterChange = () => {},
  onSearch = () => {},
  defaultValue = '',
  selectedFilterRef = null,
  searchValueRef = null,
  initialSelectedFilter = '',
  onSearchStateChange = () => {},
  showFilter = true,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(defaultValue);
  const [selectedFilter, setSelectedFilter] = useState(initialSelectedFilter);

  // defaultValue가 변경될 때 searchValue 업데이트
  useEffect(() => {
    setSearchValue(defaultValue);
  }, [defaultValue]);

  // initialSelectedFilter가 변경될 때 selectedFilter 업데이트
  useEffect(() => {
    setSelectedFilter(initialSelectedFilter);
    if (selectedFilterRef) {
      selectedFilterRef.current = initialSelectedFilter;
    }
  }, [initialSelectedFilter, selectedFilterRef]);

  // 검색 가능 상태가 변경될 때마다 부모에게 알림
  useEffect(() => {
    const canSearch = showFilter ? selectedFilter : true;
    onSearchStateChange(canSearch);
  }, [selectedFilter, onSearchStateChange, showFilter]);

  // 외부에서 호출할 수 있는 함수들을 노출
  useImperativeHandle(ref, () => ({
    reset: () => {
      setSearchValue('');
      setSelectedFilter('');
      setIsOpen(false);
      if (selectedFilterRef) {
        selectedFilterRef.current = '';
      }
      if (searchValueRef) {
        searchValueRef.current = '';
      }
    },
    getValue: () => searchValue,
    getSelectedFilter: () => selectedFilter,
  }), [selectedFilterRef, searchValueRef, searchValue, selectedFilter]);

  const handleFilterChange = (e) => {
    const { value } = e.target;
    setSelectedFilter(value);
    onFilterChange(value);
    
    // 외부 ref에 현재 선택된 필터 저장
    if (selectedFilterRef) {
      selectedFilterRef.current = value;
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    if (searchValueRef) {
      searchValueRef.current = value;
    }
  };

  // 엔터 키 입력 시 검색
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (showFilter && selectedFilter) {
        onSearch(searchValue, selectedFilter);
      } else if (!showFilter) {
        onSearch(searchValue);
      }
    }
  };

  // 검색 버튼 클릭 시 검색
  const handleSearchClick = () => {
    if (showFilter && selectedFilter) {
      onSearch(searchValue, selectedFilter);
    } else if (!showFilter) {
      onSearch(searchValue);
    }
  };

  return (
    <div className="flex flex-col search-box-container">
      <div className="flex align-center gap-5 search-box">
        <SearchIcon 
          color="var(--gray-400)"
          className={`cursor-pointer ${showFilter && !selectedFilter ? 'opacity-50' : ''}`} 
          onClick={handleSearchClick} 
        />
        {showFilter && (
          <div className="flex align-center cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
            <p className="text-b2-400 text-gray-600">{filters.find(filter => filter.id === selectedFilter)?.label || '필터 선택'}</p>
            <ArrowDownIcon className="text-gray-400" />
          </div>
        )}
        <input 
          type="text" 
          placeholder={showFilter ? (selectedFilter ? placeholder : '먼저 필터를 선택해주세요') : placeholder} 
          value={searchValue} 
          onChange={handleInputChange} 
          onKeyDown={handleKeyDown}
          disabled={showFilter && !selectedFilter}
          className="text-b2-400"
        />
      </div>
      {showFilter && isOpen && (
        <div className="flex flex-col gap-8 search-filter-box">
          {filters.length === 0 && <p className="text-b2-400 text-gray-600">필터가 없습니다.</p>}
            {filters.map(filter => (
            <div key={filter.id} className="flex align-center gap-2">
              <input 
                id={filter.id} 
                type="radio" 
                name="filter" 
                value={filter.id} 
                className="radio" 
                checked={selectedFilter === filter.id} 
                onChange={handleFilterChange} 
              />
              <label htmlFor={filter.id} className="text-b2-400 text-gray-600">{filter.label}</label>
            </div>
            ))}
        </div>
      )}
    </div>
  )
});

SearchBox.displayName = 'SearchBox';

export default SearchBox;